import React, { useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../../context/AuthContext';
import PerformanceTrackingService from '../../services/PerformanceTrackingService';

/**
 * Component that tracks user activity and reports it to the server
 */
const ActivityTracker = () => {
  const { isAuthenticated, user } = useContext(AuthContext);
  const isRecruiter = user?.userType === 'recruiter';
  
  // Use refs to store timers
  const activityTimerRef = useRef(null);
  const idleTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const activeMinutesRef = useRef(0);
  
  // Track login
  useEffect(() => {
    if (isAuthenticated && isRecruiter) {
      PerformanceTrackingService.trackLogin();

      // Set up beforeunload event to track logout
      const handleBeforeUnload = () => {
        // Use sendBeacon API for reliable tracking during page dismissal
        if (navigator.sendBeacon) {
          const blob = new Blob([JSON.stringify({ activityType: 'logout' })], { type: 'application/json' });
          navigator.sendBeacon('/api/performance/track', blob);
        } else {
          // Fallback: just log, don't use synchronous XHR
          PerformanceTrackingService.trackLogout();
        }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        // Track logout when component unmounts (async is fine here)
        PerformanceTrackingService.trackLogout();
      };
    }

    return undefined;
  }, [isAuthenticated, isRecruiter]);
  
  useEffect(() => {
    if (isAuthenticated && isRecruiter) {
      // Reset counters
      activeMinutesRef.current = 0;
      lastActivityRef.current = Date.now();

      // Function to handle user activity
      const handleActivity = () => {
        // If user was idle and is now active again
        const now = Date.now();
        const idleTime = now - lastActivityRef.current;

        // If user was idle for more than 5 minutes, consider it a new activity session
        if (idleTime > 5 * 60 * 1000) {
          // Report the active minutes before the idle period
          if (activeMinutesRef.current > 0) {
            PerformanceTrackingService.trackActiveTime(activeMinutesRef.current);
            activeMinutesRef.current = 0;
          }
        }

        // Update last activity time
        lastActivityRef.current = now;

        // Clear any existing idle timer
        if (idleTimerRef.current) {
          clearTimeout(idleTimerRef.current);
        }

        // Set a new idle timer
        idleTimerRef.current = setTimeout(() => {
          // Report active minutes when user goes idle
          if (activeMinutesRef.current > 0) {
            PerformanceTrackingService.trackActiveTime(activeMinutesRef.current);
            activeMinutesRef.current = 0;
          }
        }, 5 * 60 * 1000); // 5 minutes of inactivity
      };

      // Set up activity tracking timer (every minute)
      activityTimerRef.current = setInterval(() => {
        activeMinutesRef.current += 1;

        // Report active time every 5 minutes
        if (activeMinutesRef.current % 5 === 0) {
          PerformanceTrackingService.trackActiveTime(5);
          activeMinutesRef.current = 0;
        }
      }, 60 * 1000); // 1 minute

      // Set up event listeners for user activity
      const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];
      activityEvents.forEach(event => {
        document.addEventListener(event, handleActivity);
      });

      // Initial activity handling
      handleActivity();

      // Cleanup function
      return () => {
        // Clear timers
        if (activityTimerRef.current) {
          clearInterval(activityTimerRef.current);
        }

        if (idleTimerRef.current) {
          clearTimeout(idleTimerRef.current);
        }

        // Remove event listeners
        activityEvents.forEach(event => {
          document.removeEventListener(event, handleActivity);
        });

        // Report any remaining active minutes
        if (activeMinutesRef.current > 0) {
          PerformanceTrackingService.trackActiveTime(activeMinutesRef.current);
        }
      };
    }

    return undefined;
  }, [isAuthenticated, isRecruiter]);
  
  // This component doesn't render anything
  return null;
};

export default ActivityTracker;
